import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ConsentDialog } from "@/components/ConsentDialog";
import { Zap, Loader2, PhoneCall, Power, Pause, Play } from "lucide-react";

const Stat = ({ k, v, label }) => (
  <div className="rounded-xl bg-amber-50/70 px-4 py-3" data-testid={`autopilot-week-${k}`}>
    <div className="font-display text-2xl font-extrabold text-slate-900">{v}</div>
    <div className="text-xs text-slate-600">{label}</div>
  </div>
);

const WeekResults = ({ w }) => (
  <div className="mt-5 border-t border-amber-200 pt-4" data-testid="autopilot-week-results">
    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">This week's results (since Monday)</div>
    <div className="grid grid-cols-3 gap-3">
      <Stat k="calls" v={w.calls} label="Calls made" />
      <Stat k="appointments" v={w.appointments} label="Appointments booked" />
      <Stat k="texts" v={w.texts} label={w.texting_on ? "Texts sent" : "Texts (coming soon)"} />
    </div>
  </div>
);

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const DeityModeCard = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [consent, setConsent] = useState(false);
  const load = useCallback(() => api.get("/deity").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  if (!d) return null;

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/deity/start", { origin_url: window.location.origin, consent: true });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.success("AI Autopilot Mode is on"); load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const stop = async () => {
    setBusy(true);
    try { await api.post("/deity/stop"); toast.success("AI Autopilot Mode turned off"); load(); } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const pause = async (p) => {
    setBusy(true);
    try { await api.post(p ? "/deity/pause" : "/deity/resume"); toast.success(p ? "AI Autopilot Mode paused" : "AI Autopilot Mode resumed"); load(); } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <Card className="mb-8 overflow-hidden rounded-2xl border-amber-300 p-6" data-testid="deity-card">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-amber-500" />
            <h3 className="font-display text-xl font-extrabold text-slate-900">AI Autopilot Mode</h3>
            <Badge className={d.active && !d.paused ? "border-0 bg-emerald-100 text-emerald-800" : d.paused ? "border-0 bg-amber-100 text-amber-800" : "border-0 bg-slate-100 text-slate-600"} data-testid="deity-status-badge">{!d.active ? "OFF" : d.paused ? "PAUSED" : "ON"}</Badge>
          </div>
          <p className="mt-1 max-w-xl text-sm text-slate-600">AI calls 20 of your imported leads at once, then the next 20, until everyone is called — every day ({d.window}). AI-generated prospects from lead runs are not called. Interested prospects are warm-transferred to you. Every day (8 AM–8 PM ET) Autopilot also runs a free HELOC search for 1 new lead in a random Florida county. Use Pause to stop calls overnight; your subscription keeps running.</p>
          {d.active && (
            <div className="mt-3 flex flex-wrap gap-4 text-sm" data-testid="deity-stats">
              <span><b data-testid="deity-in-progress">{d.in_progress}</b> live now</span>
              <span><b data-testid="deity-called-today">{d.called_today}</b> called today</span>
              <span><b data-testid="deity-remaining">{d.remaining_today}</b> left today</span>
              {d.note && <span className="text-slate-500" data-testid="deity-note">{d.paused ? "Paused - no calls until you resume" : d.note}</span>}
            </div>
          )}
        </div>
        {d.active
          ? <div className="flex shrink-0 gap-2">
              <Button onClick={() => pause(!d.paused)} disabled={busy} variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="deity-pause-btn">{d.paused ? <><Play className="mr-1.5 h-4 w-4" /> Resume</> : <><Pause className="mr-1.5 h-4 w-4" /> Pause</>}</Button>
              <Button onClick={stop} disabled={busy} variant="outline" className="border-red-200 text-red-700 hover:bg-red-50" data-testid="deity-stop-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Power className="mr-1.5 h-4 w-4" /> Turn off</>}</Button>
            </div>
          : <Button onClick={() => setConsent(true)} disabled={busy} className="gold-cta shrink-0 border-0 font-bold" data-testid="deity-start-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><PhoneCall className="mr-1.5 h-4 w-4" /> Turn on — {money(d.my_price)}/month</>}</Button>}
      </div>
      {d.week && <WeekResults w={d.week} />}
      <ConsentDialog open={consent} onOpenChange={setConsent} onConfirm={start} testId="deity-consent" title="Consent required for AI Autopilot Mode" confirmLabel="Continue"
        description="AI / artificial-voice calls are regulated by the TCPA and state telemarketing laws. AI Autopilot Mode is a monthly subscription that renews automatically until you turn it off."
        statement="I confirm I have the consent required under the TCPA and applicable state law to call every prospect on my list using an AI / artificial voice, every day, until I turn AI Autopilot Mode off, and for the AI to leave a voicemail if they don't answer." />
    </Card>
  );
};
